import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import SourceBadge from './SourceBadge';
import 'leaflet/dist/leaflet.css';
import MapContextMenu from './MapContextMenu';
import MapLayerControl from './MapLayerControl';
import FieldDrawingForm from './FieldDrawingForm';
import FieldEditForm from './FieldEditForm';
import PoiForm from './PoiForm';
import FieldDeleteConfirm from './FieldDeleteConfirm';
import FieldsManagementView from './FieldsManagementView';
import ShapefileImport from './ShapefileImport';

const LeafletMap = lazy(() => import('./MapViewLeaflet'));

// Legacy LAYERS const removed — replaced by MapLayerControl groups
const STATUS_COLORS = { good: '#22c55e', watch: '#f59e0b', problem: '#ef4444' };
const STATUS_LABELS_KEY = { good: 'map.statusGood', watch: 'map.statusWatch', problem: 'map.statusProblem' };

const GEO_CACHE = { data: null, ts: 0 };
const CACHE_TTL_MS = 5 * 60 * 1000;
export const resetGeoCache = () => { GEO_CACHE.data = null; GEO_CACHE.ts = 0; };

function formatEur(value) {
  if (value == null) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

const SPRAY_CACHE = { data: null, ts: 0 };
const SPRAY_CACHE_TTL_MS = 30 * 60 * 1000;
const SPRAY_COLORS = { green: '#22c55e', yellow: '#f59e0b', red: '#ef4444' };

export default function MapView({ token, onOpenFieldDetail, onCreateTask, onCreateActivity, onFieldCreated, pendingFlyToFieldId, onFlyToConsumed, isFeatureEnabled }) {
  const { t } = useTranslation();
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // UX-P3: 3-group layer control replaces flat coloring
  const [coloring, setColoring] = useState('crop');       // Einfärbung (radio)
  const [overlays, setOverlays] = useState(() => new Set(['boundaries', 'pois'])); // Anzeigen (checkboxes)
  const [selectedField, setSelectedField] = useState(null);
  const [sprayData, setSprayData] = useState(null);

  // Context menus
  const [contextMenu, setContextMenu] = useState(null);         // { field, x, y } — field marker
  const [emptyContextMenu, setEmptyContextMenu] = useState(null); // { latlng, x, y } — empty area

  // Drawing / editing / deleting
  const [drawingActive, setDrawingActive] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState(null);       // GeoJSON after user finishes drawing
  const [editingField, setEditingField] = useState(null);       // field being polygon-edited
  const [deletingField, setDeletingField] = useState(null);     // field pending delete confirm

  // Field edit form (attribute editing — BUG 3)
  const [editFormField, setEditFormField] = useState(null);     // field being attribute-edited

  // Fields management table (BUG 6)
  const [showManagement, setShowManagement] = useState(false);

  // Shapefile import (Phase 1 Sprint 1)
  const [showImport, setShowImport] = useState(false);

  // POI
  const [pois, setPois] = useState([]);
  const [poiFormData, setPoiFormData] = useState(null);         // { latlng, defaultType }

  // Tile source toggle (osm / satellite)
  const [tileSource, setTileSource] = useState(() => localStorage.getItem('aif_map_tiles') || 'osm');

  // Drawing instruction banner
  const [drawBanner, setDrawBanner] = useState(false);

  // Empty-area latlng for poi/draw
  const emptyLatLngRef = useRef(null);

  const fetchGeoData = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && GEO_CACHE.data && now - GEO_CACHE.ts < CACHE_TTL_MS) {
      setGeoData(GEO_CACHE.data);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/v1/aif/farm/fields/geo');
      const data = res?.data ?? res;
      GEO_CACHE.data = data;
      GEO_CACHE.ts = Date.now();
      setGeoData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchPois = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/aif/pois');
      const data = res?.data ?? res;
      setPois(data.pois ?? []);
    } catch (_) { /* non-critical */ }
  }, [token]);

  const fetchSprayData = useCallback(async () => {
    const now = Date.now();
    if (SPRAY_CACHE.data && now - SPRAY_CACHE.ts < SPRAY_CACHE_TTL_MS) {
      setSprayData(SPRAY_CACHE.data);
      return;
    }
    try {
      const res = await apiClient.get('/api/v1/aif/weather/spray-map');
      const data = res?.data ?? res;
      SPRAY_CACHE.data = data;
      SPRAY_CACHE.ts = Date.now();
      setSprayData(data);
    } catch (_) { /* non-critical */ }
  }, [token]);

  useEffect(() => { fetchGeoData(); fetchPois(); }, [fetchGeoData, fetchPois]);
  useEffect(() => { if (coloring === 'spray') fetchSprayData(); }, [coloring, fetchSprayData]);
  useEffect(() => {
    if (coloring !== 'spray') return;
    const interval = setInterval(fetchSprayData, SPRAY_CACHE_TTL_MS);
    return () => clearInterval(interval);
  }, [coloring, fetchSprayData]);

  // G.10: resolve pending fly-to after fields are loaded
  useEffect(() => {
    if (!pendingFlyToFieldId || !geoData?.fields?.length) return;
    const target = geoData.fields.find(f => f.id === pendingFlyToFieldId);
    if (!target || target.latitude == null || target.longitude == null) {
      onFlyToConsumed?.();
      return;
    }
    setSelectedField(target);
  }, [pendingFlyToFieldId, geoData]);

  const fieldsWithGps = geoData?.fields?.filter(f => f.latitude && f.longitude) ?? [];
  const fieldsWithoutGps = geoData?.fields?.filter(f => !f.latitude || !f.longitude) ?? [];
  const total = geoData?.farm_total;

  const sprayByFieldId = {};
  if (sprayData?.fields) {
    sprayData.fields.forEach(sf => { sprayByFieldId[sf.field_id] = sf; });
  }

  const getFieldColor = (field) => {
    if (coloring === 'none') return '#9E9E9E';
    if (coloring === 'spray') {
      const sf = sprayByFieldId[field.id];
      return sf ? (SPRAY_COLORS[sf.spray_color] ?? '#9E9E9E') : '#9E9E9E';
    }
    if (coloring === 'status') return STATUS_COLORS[field.status] ?? '#9E9E9E';
    if (coloring === 'yield') {
      if (!field.field_value_eur || !field.area_ha) return '#9E9E9E';
      const dbPerHa = field.field_value_eur / field.area_ha;
      if (dbPerHa >= 2000) return '#22c55e';
      if (dbPerHa >= 1000) return '#f59e0b';
      return '#ef4444';
    }
    return field.crop_color ?? '#9E9E9E';
  };

  const getSprayTooltip = (field) => {
    if (coloring !== 'spray') return null;
    const sf = sprayByFieldId[field.id];
    return sf?.spray_reason ?? null;
  };

  const getRadius = (area_ha) => {
    if (!area_ha) return 16;
    return Math.max(14, Math.min(40, 14 + Math.sqrt(area_ha) * 2));
  };

  // ── Drawing handlers ───────────────────────────────────────────────────────
  const handleStartDrawing = () => {
    setEmptyContextMenu(null);
    setContextMenu(null);
    setDrawingActive(true);
    setDrawBanner(true);
  };

  const handleDrawComplete = (geojson) => {
    setDrawingActive(false);
    setDrawBanner(false);
    setDrawnPolygon(geojson);
  };

  const handleFieldSaved = (newField) => {
    setDrawnPolygon(null);
    resetGeoCache();
    fetchGeoData(true);
    // BUG 7: auto-create chat thread for the new field
    onFieldCreated?.(newField.id, newField.name);
  };

  const handleDrawCancel = () => {
    setDrawingActive(false);
    setDrawBanner(false);
    setDrawnPolygon(null);
  };

  // ── POI handlers ────────────────────────────────────────────────────────────
  const handleAddPoi = (defaultType = 'other') => {
    const latlng = emptyLatLngRef.current;
    setEmptyContextMenu(null);
    if (!latlng) return;
    setPoiFormData({ latlng, defaultType });
  };

  const handlePoiSaved = (newPoi) => {
    setPois(prev => [...prev, newPoi]);
    setPoiFormData(null);
  };

  // ── Field edit / delete ─────────────────────────────────────────────────────
  const handleEditField = (field) => {
    setContextMenu(null);
    setEditFormField(field);
  };

  const handleEditFormSaved = (updatedField) => {
    setEditFormField(null);
    resetGeoCache();
    fetchGeoData(true);
  };

  const handleEditSave = async (geojson) => {
    try {
      await apiClient.patch(`/api/v1/aif/farm/fields/${editingField.id}`, { polygon_geojson: geojson }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      resetGeoCache();
      fetchGeoData(true);
    } catch { /* ApiError */ }
    setEditingField(null);
  };

  const handleDeleteField = (field) => {
    setContextMenu(null);
    setDeletingField(field);
  };

  const handleFieldDeleted = () => {
    setDeletingField(null);
    setSelectedField(null);
    resetGeoCache();
    fetchGeoData(true);
  };

  const allFields = geoData?.fields ?? [];

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[var(--aif-bg-page)]">

      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-3">
            <div className="text-2xl">⚠️</div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('map.loadError')}</p>
            <button onClick={() => fetchGeoData(true)} className="text-xs text-[var(--aif-color-primary)] underline">
              {t('app.retry')}
            </button>
          </div>
        </div>
      )}

      {!loading && !error && geoData && fieldsWithGps.length === 0 && (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-4 max-w-xs">
            <div className="text-4xl">🗺️</div>
            <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{t('map.noGpsTitle')}</h3>
            <p className="text-sm text-[var(--aif-text-muted)] leading-relaxed">{t('map.noGpsHint')}</p>
            {geoData.fields.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs text-[var(--aif-text-muted)]">{t('map.noFieldsHint')}</p>
                <button
                  onClick={handleStartDrawing}
                  className="px-4 py-2.5 text-sm font-medium rounded-xl bg-[var(--aif-color-primary)] text-white hover:opacity-90 transition min-h-[var(--aif-touch-min)]"
                  data-testid="map-draw-cta"
                >
                  {t('map.drawFieldCta')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && !error && fieldsWithGps.length > 0 && (
        <div className="relative flex-1 min-h-0">

          {/* Inventory value overlay */}
          <div className="absolute top-3 left-3 right-3 z-[var(--aif-z-map-overlay)] flex items-start justify-between gap-2 pointer-events-none">
            <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 pointer-events-auto">
              {coloring === 'spray' && sprayData?.summary ? (
                <>
                  <div className="text-[11px] text-[var(--aif-text-muted)] leading-none mb-0.5">{t('map.layerSpray')}</div>
                  <div className="text-base font-bold text-[var(--aif-text-primary)] leading-tight" data-testid="spray-map-overlay">
                    {sprayData.summary.good}/{sprayData.summary.total} {sprayData.summary.good === sprayData.summary.total ? '\uD83D\uDFE2' : sprayData.summary.good > 0 ? '\uD83D\uDFE1' : '\uD83D\uDD34'}
                  </div>
                  {sprayData.next_rain && (
                    <div className="text-[10px] text-blue-400">💧 {t('weather.nextRain')}: {sprayData.next_rain}</div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-[11px] text-[var(--aif-text-muted)] leading-none mb-0.5">{t('map.inventoryLabel')}</div>
                  <div className="text-base font-bold text-[var(--aif-text-primary)] leading-tight">{formatEur(total?.total_field_value)}</div>
                  <div className="text-[10px] text-[var(--aif-text-muted)]">
                    {total?.field_count} {t('map.fieldsLabel')} · {total?.total_area_ha?.toLocaleString('de-DE')} ha
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowManagement(true); }}
                    className="block text-[10px] text-[var(--aif-color-primary)] font-medium mt-0.5 hover:underline min-h-[44px] py-1"
                  >
                    {t('map.manageFields')}
                  </button>
                </>
              )}
            </div>

            {/* UX-P3: 3-group layer control */}
            <MapLayerControl
              tileSource={tileSource}
              onTileSourceChange={(src) => { setTileSource(src); localStorage.setItem('aif_map_tiles', src); }}
              coloring={coloring}
              onColoringChange={setColoring}
              overlays={overlays}
              onOverlayToggle={(id) => setOverlays(prev => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              })}
              poiCount={pois.length}
              isFeatureEnabled={isFeatureEnabled}
            />
          </div>

          {/* Drawing instruction banner */}
          {drawBanner && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[var(--aif-z-map-banner)] pointer-events-none">
              <div className="bg-[var(--aif-color-primary)] text-white rounded-full px-4 py-2 text-sm font-medium shadow-lg flex items-center gap-2">
                <span>✏️</span>
                <span>{t('ui.drawFieldHint')}</span>
                <button
                  onClick={handleDrawCancel}
                  className="pointer-events-auto ml-1 text-white/80 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Edit mode banner */}
          {editingField && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[var(--aif-z-map-banner)] pointer-events-auto">
              <div className="bg-orange-500 text-white rounded-full px-4 py-2 text-sm font-medium shadow-lg flex items-center gap-3">
                <span>{t('map.editPolygon', { name: editingField.name })}</span>
                <button
                  onClick={() => {
                    // Trigger save via map event — handled by EditPolygonController
                    // We need a ref to the map to call fireEvent
                    // For simplicity, just call setEditingField(null) to cancel
                    setEditingField(null);
                  }}
                  className="text-white/80 hover:text-white text-xs underline"
                >
                  {t('ui.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Leaflet map */}
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center bg-[var(--aif-bg-elevated)]">
              <div className="w-8 h-8 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
            </div>
          }>
            <LeafletMap
              fields={allFields}
              pois={overlays.has('pois') ? pois : []}
              showBoundaries={overlays.has('boundaries')}
              tileSource={tileSource}
              onPoiClick={(poi) => { setSelectedField(null); setContextMenu(null); setEmptyContextMenu(null); /* TODO Phase 3.7: open POI detail panel */ }}
              getFieldColor={getFieldColor}
              getRadius={getRadius}
              onFieldTap={(field) => { setSelectedField(field); setContextMenu(null); setEmptyContextMenu(null); }}
              selectedFieldId={selectedField?.id}
              getSprayTooltip={getSprayTooltip}
              onFieldLongPress={(field, x, y) => {
                setSelectedField(null);
                setEmptyContextMenu(null);
                setContextMenu({ field, x, y });
              }}
              onEmptyAreaContext={(latlng, x, y) => {
                emptyLatLngRef.current = latlng;
                setContextMenu(null);
                setSelectedField(null);
                setEmptyContextMenu({ latlng, x, y });
              }}
              drawingActive={drawingActive}
              onDrawComplete={handleDrawComplete}
              onDrawCancel={handleDrawCancel}
              editingField={editingField}
              onEditSave={handleEditSave}
              onEditCancel={() => setEditingField(null)}
              onFlyToTriggered={onFlyToConsumed}
            />
          </Suspense>

          {/* Field bottom sheet */}
          {selectedField && !drawnPolygon && !poiFormData && (
            <FieldBottomSheet
              field={selectedField}
              onClose={() => setSelectedField(null)}
              onOpenDetail={onOpenFieldDetail}
            />
          )}

          {/* Field drawing form (after polygon drawn) */}
          {drawnPolygon && (
            <FieldDrawingForm
              polygon={drawnPolygon}
              token={token}
              onSave={handleFieldSaved}
              onCancel={handleDrawCancel}
            />
          )}

          {/* Field edit form (attribute editing — BUG 3) */}
          {editFormField && (
            <FieldEditForm
              field={editFormField}
              token={token}
              onSave={handleEditFormSaved}
              onCancel={() => setEditFormField(null)}
            />
          )}

          {/* POI form */}
          {poiFormData && (
            <PoiForm
              latlng={poiFormData.latlng}
              defaultType={poiFormData.defaultType}
              token={token}
              onSave={handlePoiSaved}
              onCancel={() => setPoiFormData(null)}
            />
          )}

          {/* Field context menu (right-click on field marker) */}
          {contextMenu && (
            <MapContextMenu
              mode="field"
              field={contextMenu.field}
              x={contextMenu.x}
              y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              onCreateTask={(fieldId) => { setContextMenu(null); onCreateTask?.(fieldId, contextMenu?.field?.name); }}
              onCreateActivity={(fieldId) => { setContextMenu(null); onCreateActivity?.(fieldId, contextMenu?.field?.name); }}
              onUploadPhoto={() => setContextMenu(null)}
              onShareWorker={() => setContextMenu(null)}
              onShareExternal={() => setContextMenu(null)}
              onOpenDetail={(fieldId) => { const name = contextMenu?.field?.name; setContextMenu(null); onOpenFieldDetail?.(fieldId, name); }}
              onEditField={handleEditField}
              onDeleteField={handleDeleteField}
            />
          )}

          {/* Empty area context menu (right-click on empty map) */}
          {emptyContextMenu && (
            <MapContextMenu
              mode="empty"
              x={emptyContextMenu.x}
              y={emptyContextMenu.y}
              onClose={() => setEmptyContextMenu(null)}
              onDrawField={handleStartDrawing}
              onAddPoi={handleAddPoi}
              onUploadPhoto={() => setEmptyContextMenu(null)}
              onImportFields={() => { setEmptyContextMenu(null); setShowImport(true); }}
              onManageFields={() => { setEmptyContextMenu(null); setShowManagement(true); }}
            />
          )}
        </div>
      )}

      {/* Fields management view (full overlay — BUG 6) */}
      {showManagement && (
        <div className="fixed inset-0 z-[var(--aif-z-detail-panel)] bg-[var(--aif-bg-page)] flex flex-col">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--aif-border-default)]">
            <button
              onClick={() => setShowManagement(false)}
              className="p-2 rounded-xl hover:bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm"
            >
              {t('ui.back')}
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <FieldsManagementView
              token={token}
              onCreateField={() => { setShowManagement(false); handleStartDrawing(); }}
              onEditField={(field) => { setShowManagement(false); handleEditField(field); }}
              onDeleteField={(field) => { setShowManagement(false); handleDeleteField(field); }}
              onOpenDetail={(fieldId, name) => { setShowManagement(false); onOpenFieldDetail?.(fieldId, name); }}
              onImportFields={() => { setShowManagement(false); setShowImport(true); }}
            />
          </div>
        </div>
      )}

      {/* Shapefile import overlay (Phase 1 Sprint 1) */}
      {showImport && (
        <div className="fixed inset-0 z-[var(--aif-z-detail-panel)] bg-[var(--aif-bg-page)]">
          <ShapefileImport
            token={token}
            onDone={() => { setShowImport(false); resetGeoCache(); fetchGeoData(true); }}
          />
        </div>
      )}

      {/* Field delete confirmation modal */}
      {deletingField && (
        <FieldDeleteConfirm
          field={deletingField}
          token={token}
          onDeleted={handleFieldDeleted}
          onCancel={() => setDeletingField(null)}
        />
      )}
    </div>
  );
}

function FieldBottomSheet({ field, onClose, onOpenDetail }) {
  const { t } = useTranslation();
  const statusColor = STATUS_COLORS[field.status] ?? '#9E9E9E';
  const statusLabel = field.status ? t(STATUS_LABELS_KEY[field.status] ?? 'map.statusGood') : '';

  return (
    <div
      data-testid="field-bottom-sheet"
      className="absolute bottom-0 left-0 right-0 z-[var(--aif-z-bottom-sheet)] bg-[var(--aif-bg-surface)] rounded-t-2xl shadow-2xl
                 border-t border-[var(--aif-border-default)] animate-slideUp"
      style={{ height: 210 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-center pt-2 pb-1">
        <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
      </div>
      <button
        onClick={onClose}
        className="absolute top-3 right-4 w-7 h-7 flex items-center justify-center rounded-full
                   text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)] text-sm"
      >
        ✕
      </button>
      <div className="px-4 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-[var(--aif-text-primary)] text-base leading-tight">{field.name}</h3>
            <p className="text-sm text-[var(--aif-text-muted)]">
              {field.current_crop_name ?? '–'} · {field.area_ha?.toLocaleString('de-DE')} ha
              {field.has_polygon && <span className="ml-1 text-[10px] text-blue-400">📐</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor }} />
            <span className="text-xs text-[var(--aif-text-muted)]">{statusLabel}</span>
          </div>
        </div>

        <div className="flex items-center justify-between bg-[var(--aif-bg-elevated)] rounded-xl px-3 py-2 mb-3">
          <div>
            <div className="text-[11px] text-[var(--aif-text-muted)]">{t('map.fieldValueLabel')}</div>
            <div className="text-lg font-bold text-[var(--aif-text-primary)]">
              {field.field_value_eur != null
                ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(field.field_value_eur)
                : '–'}
            </div>
          </div>
          <div className="text-right text-[11px] text-[var(--aif-text-muted)] space-y-0.5">
            <div>
              {field.expected_yield_t_ha?.toLocaleString('de-DE')} t/ha
              <SourceBadge source={field.yield_source || 'reference'} variant="inline" className="ml-1" />
            </div>
            <div>
              {field.market_price_eur_t?.toLocaleString('de-DE')} EUR/t
              <SourceBadge source={field.price_source || 'reference'} variant="inline" className="ml-1" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          {field.open_task_count > 0
            ? <span className="flex items-center gap-1 text-[11px] bg-red-500/10 text-red-500 px-2 py-0.5 rounded-full font-medium">
                ✅ {t('map.openTaskCount', { count: field.open_task_count })}
              </span>
            : <span className="text-[11px] text-[var(--aif-text-muted)]">{t('ui.noOpenTasks')}</span>}
        </div>

        {onOpenDetail && (
          <button
            data-testid="field-detail-button"
            onClick={() => { onOpenDetail?.(field.id, field.name); onClose(); }}
            className="w-full py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                       hover:opacity-90 transition-opacity"
          >
            {t('map.detailsButton')} →
          </button>
        )}
      </div>
    </div>
  );
}
